import { IonBadge, IonButton, IonCol, IonGrid, IonItem, IonList, IonLoading, IonRow } from '@ionic/react';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import React, { useEffect, useMemo, useState } from 'react';
import Content from './components/Content';
import { useProfile } from './data/store/useProfile';
import { useRegions } from './data/store/useRegions';
import { usePageTitle } from './util/sugar';

interface RegionListProps { }

const RegionList: React.FC<RegionListProps> = () => {
  const { regions } = useRegions();
  const [regionCounts, setRegionCounts] = useState<Record<string, number>>({});
  const [loadingCounts, setLoadingCounts] = useState(true);

  usePageTitle('Regions');
  const unauthenticated = useProfile(x => x.status === 'unauthenticated');
  const authorId = useProfile(x => x.authorId);
  const [limit, setLimit] = useState<number>(10);
  const pageSize = 20;

  useEffect(() => {
    const db = getFirestore();
    const statsRef = doc(db, 'stats', 'regions');
    const unsubscribe = onSnapshot(
      statsRef,
      (snapshot) => {
        setRegionCounts(snapshot.exists() ? snapshot.data() || {} : {});
        setLoadingCounts(false);
      },
      (error) => {
        console.error('Error fetching region counts:', error);
        setRegionCounts({});
        setLoadingCounts(false);
      }
    );
    return unsubscribe;
  }, []);

  // Sort and filter regions based on stats counts. If the stats/regions
  // doc is empty (e.g. before the Cloud Function has ever fired for this
  // project, or while it's still catching up), fall back to showing every
  // known region alphabetically so the page is never blank.
  const sortedRegions = useMemo(() => {
    const hasAnyCounts = Object.values(regionCounts).some(
      (n) => typeof n === "number" && n > 0,
    );
    const allRegions = Object.values(regions).filter(
      (region) => region.id !== "XXX",
    );

    if (hasAnyCounts) {
      return allRegions
        .filter((region) => regionCounts[region.id] > 0)
        .sort((a, b) => {
          const countA = regionCounts[a.id] || 0;
          const countB = regionCounts[b.id] || 0;
          return countB - countA; // Descending by count
        });
    }

    // No stats yet — show nothing rather than an unpopulated full list.
    return [];
  }, [regions, regionCounts]);

  return (
    <Content className='chill'>
      <IonGrid>
        <IonRow>
          <IonLoading isOpen={loadingCounts} />
          <IonCol sizeMd='6' offsetMd='3' sizeXs='12'>
            <IonList>
              {sortedRegions.slice(0, limit).map(({city, id}) => {
                const count = regionCounts[id];
                return (
                  <IonItem key={id} routerLink={`/region-artists/${id}`}>
                    {city}
                    {typeof count === "number" && count > 0 && (
                      <IonBadge slot="end" color="primary">
                        {count} {count === 1 ? 'artist' : 'artists'}
                      </IonBadge>
                    )}
                  </IonItem>
                );
              })}
              {sortedRegions.length > limit && (
                <IonButton fill='outline' expand='full' onClick={() => setLimit(x => x + pageSize)}>
                  Load More
                </IonButton>
              )}
            </IonList>
          </IonCol>
        </IonRow>
      </IonGrid>
    </Content>
  );
};

export default RegionList;